/**
 * User-facing copy for Stop / Force-End backend error codes.
 *
 * Backend routes emit small, stable machine codes:
 *   - `CHARGER_STILL_DELIVERING_POWER`  — the meter is still ticking up.
 *   - `OCPP_SESSION_STILL_ACTIVE`       — legacy shape; treated same as above.
 *   - `OCPP_ERROR`                      — RemoteStopTransaction failed
 *                                          to reach the charger.
 *   - `SESSION_NOT_FOUND` / `INVALID_STATUS` — client state drift.
 *   - `INTERNAL_ERROR`, `FORCE_END_FAILED` — server-side unexpected.
 *
 * The user never sees any of those strings. This mapper is the single
 * translation layer for both the Stop flow and the Force End flow. New
 * error codes get one line here rather than any UI branch.
 */

const KNOWN = {
  CHARGER_STILL_DELIVERING_POWER: {
    title: 'Charger still delivering power',
    message:
      'Your charger reports it is still charging the vehicle. Please unplug the vehicle first, then try again.',
  },

  // The pre-fix backend threw this from the broader OCPP-active guard.
  // Kept in the map so any deployment lag where the old code is still
  // running does not surface the raw string to the customer.
  OCPP_SESSION_STILL_ACTIVE: {
    title: 'Charger still delivering power',
    message:
      'Your charger reports it is still charging the vehicle. Please unplug the vehicle first, then try again.',
  },

  OCPP_ERROR: {
    title: "We couldn't reach your charger",
    message:
      'The stop command did not reach the charger. You can force end the session — billing will finalize using the last available meter reading, and you may still need to unplug the vehicle.',
  },

  SESSION_NOT_FOUND: {
    title: 'Session not found',
    message:
      "We couldn't find this charging session. It may have already ended.",
  },

  INVALID_STATUS: {
    title: 'Session already ended',
    message:
      'This session is already in a final state and does not need to be stopped again.',
  },

  FORCE_END_FAILED: {
    title: 'Force end failed',
    message:
      "We couldn't finalize your session right now. Please try again in a moment, or contact support if the problem persists.",
  },

  INTERNAL_ERROR: {
    title: "Something went wrong",
    message:
      "We couldn't process that request. Please try again in a moment.",
  },
};

const GENERIC = {
  title: "We couldn't complete that action",
  message:
    'Please try again in a moment. If the problem persists, contact support.',
};

/**
 * Translate a backend error result — `{ error, message }` — into
 * customer-friendly copy. Never returns the raw machine code; falls
 * back to a generic message when the code is unknown.
 *
 * @param {{error?: string, message?: string} | null | undefined} result
 * @returns {{ title: string, message: string, code: string | null }}
 */
export function translateStopError(result) {
  const code = typeof result?.error === 'string' ? result.error : null;
  const known = code ? KNOWN[code] : null;
  if (known) {
    return { title: known.title, message: known.message, code };
  }
  return { title: GENERIC.title, message: GENERIC.message, code };
}
